"use client";

import { useLayoutEffect, useRef, useState } from "react";
import Image from "next/image";
import parse from "html-react-parser";
import { IPageContent } from "@/types/pageContent.types";
import { motion, useScroll, useTransform } from "framer-motion";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

import styles from "./styles.module.scss";
import TransitionLink from "@/components/TransitionLink";

const MotionImage = motion.create(Image);

gsap.registerPlugin(ScrollTrigger);

export type IonAcqAppViewProps = {
  ionAcqAppSection1Data: IPageContent["ionAcqAppPageModel"]["ionAcqAppPage"]["ionAcqAppSection1"];
  ionAcqAppSection2Data: IPageContent["ionAcqAppPageModel"]["ionAcqAppPage"]["ionAcqAppSection2"];
  ionAcqAppSection3Data: IPageContent["ionAcqAppPageModel"]["ionAcqAppPage"]["ionAcqAppSection3"];
  ionAcqAppSection4Data: IPageContent["ionAcqAppPageModel"]["ionAcqAppPage"]["ionAcqAppSection4"];
  ionAcqAppSection5Data: IPageContent["ionAcqAppPageModel"]["ionAcqAppPage"]["ionAcqAppSection5"];
  ionAcqAppSection6Data: IPageContent["ionAcqAppPageModel"]["ionAcqAppPage"]["ionAcqAppSection6"];
  ionAcqAppSection7Data: IPageContent["ionAcqAppPageModel"]["ionAcqAppPage"]["ionAcqAppSection7"];
  ionAcqAppSection8Data: IPageContent["ionAcqAppPageModel"]["ionAcqAppPage"]["ionAcqAppSection8"];
};

type IonAcqAppSection6Item = IonAcqAppViewProps["ionAcqAppSection6Data"][number];
type IonAcqAppSection6Heading = Extract<IonAcqAppSection6Item, { __typename: "HeadingRecord" }>;
type IonAcqAppSection6Bullet = Extract<
  IonAcqAppSection6Item,
  { __typename: "BulletNumberBulletTextHeadingBodyGalleryRecord" }
>;

const AcqAppView: React.FC<IonAcqAppViewProps> = ({
  ionAcqAppSection1Data,
  ionAcqAppSection2Data,
  ionAcqAppSection3Data,
  ionAcqAppSection4Data,
  ionAcqAppSection5Data,
  ionAcqAppSection6Data,
  ionAcqAppSection7Data,
  ionAcqAppSection8Data
}) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [section6ActiveIndex, setSection6ActiveIndex] = useState(0);
  const stickyRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const stickyPanelsRef = useRef<Array<HTMLDivElement | null>>([]);
  const stickyStepsRef = useRef<Array<HTMLDivElement | null>>([]);
  const stickyCurrentPanelIndexRef = useRef(0);
  const stickyScrollTweenRef = useRef<gsap.core.Tween | null>(null);
  const stickyJumpTargetRef = useRef<number | null>(null);
  const stickyJumpingRef = useRef(false);
  const stickyJumpDelayTimerRef = useRef<number | null>(null);
  const section2Ref = useRef<HTMLElement>(null);
  const section8Ref = useRef<HTMLElement>(null);
  const section6StickyRef = useRef<HTMLDivElement>(null);
  const section6WrapperRef = useRef<HTMLDivElement>(null);
  const section6PanelsRef = useRef<Array<HTMLDivElement | null>>([]);
  const section6StepsRef = useRef<Array<HTMLDivElement | null>>([]);

  // Section 2 : Once spaces are mapped...
  const { scrollYProgress: section2ScrollProgress } = useScroll({
    target: section2Ref,
    offset: ["start end", "end start"],
  });
  const section2BackgroundY = useTransform(
    section2ScrollProgress,
    [0, 1],
    [-100, 100]
  );
  const { scrollYProgress: section8ScrollProgress } = useScroll({
    target: section8Ref,
    offset: ["start end", "end start"],
  });
  const section8BackgroundY = useTransform(
    section8ScrollProgress,
    [0, 1],
    [-80, 80]
  );

  // Section 3 - 5 : Automated Floor Plan...
  const stickyContent = [
    {
      data: ionAcqAppSection3Data,
      id: "section3",
      imagePosition: "left" as const,
    },
    {
      data: ionAcqAppSection4Data,
      id: "section4",
      imagePosition: "left" as const,
    },
    {
      data: ionAcqAppSection5Data,
      id: "section5",
      imagePosition: "left" as const,
    }
  ];

  const section6Heading = ionAcqAppSection6Data.find(
    (item): item is IonAcqAppSection6Heading => item.__typename === "HeadingRecord"
  );
  const section6Content = ionAcqAppSection6Data.filter(
    (item): item is IonAcqAppSection6Bullet =>
      item.__typename === "BulletNumberBulletTextHeadingBodyGalleryRecord"
  );

  const transitionStickyPanel = (
    panels: HTMLDivElement[],
    nextPanelIndex: number
  ) => {
    const currentPanelIndex = stickyCurrentPanelIndexRef.current;

    if (nextPanelIndex === currentPanelIndex) {
      return;
    }

    gsap.to(panels[currentPanelIndex], {
      autoAlpha: 0,
      duration: 0.35,
      ease: "power2.out",
      overwrite: "auto",
    });

    gsap.fromTo(
      panels[nextPanelIndex],
      { autoAlpha: 0 },
      {
        autoAlpha: 1,
        duration: 0.45,
        ease: "power2.out",
        overwrite: "auto",
      }
    );

    stickyCurrentPanelIndexRef.current = nextPanelIndex;
    setActiveIndex(nextPanelIndex);
  };

  const scrollToStickyPanel = (index: number) => {
    const step = stickyStepsRef.current[index];
    const wrapper = wrapperRef.current;
    if (!step || !wrapper) {
      return;
    }

    const wrapperTop = wrapper.getBoundingClientRect().top + window.scrollY;
    const stepTop = step.getBoundingClientRect().top + window.scrollY;
    const preferredTarget = index === 0
      ? wrapperTop
      : stepTop - window.innerHeight * 0.25;
    const targetY = Math.max(wrapperTop, preferredTarget);

    const panels = stickyPanelsRef.current.filter(
      (panel): panel is HTMLDivElement => panel !== null
    );

    if (!panels.length) {
      return;
    }

    if (stickyJumpDelayTimerRef.current !== null) {
      window.clearTimeout(stickyJumpDelayTimerRef.current);
      stickyJumpDelayTimerRef.current = null;
    }
    stickyScrollTweenRef.current?.kill();
    stickyJumpingRef.current = true;
    stickyJumpTargetRef.current = index;

    stickyJumpDelayTimerRef.current = window.setTimeout(() => {
      stickyJumpDelayTimerRef.current = null;
      transitionStickyPanel(panels, index);
    }, 500);

    const state = { y: window.scrollY };
    stickyScrollTweenRef.current = gsap.to(state, {
      y: targetY,
      duration: 0.55,
      ease: "power2.out",
      overwrite: true,
      onUpdate: () => {
        window.scrollTo(0, state.y);
      },
      onComplete: () => {
        stickyJumpingRef.current = false;
        stickyJumpTargetRef.current = null;
        stickyScrollTweenRef.current = null;
      },
      onInterrupt: () => {
        stickyJumpingRef.current = false;
        stickyJumpTargetRef.current = null;
        stickyScrollTweenRef.current = null;
      },
    });
  };

  useLayoutEffect(() => {
    if (!wrapperRef.current || !stickyRef.current || stickyContent.length <= 1) return;

    const panels = stickyPanelsRef.current.filter(
      (panel): panel is HTMLDivElement => panel !== null
    );
    const steps = stickyStepsRef.current.filter(
      (step): step is HTMLDivElement => step !== null
    );

    if (!panels.length || !steps.length) return;

    const showPanel = (nextPanelIndex: number) => {
      if (nextPanelIndex === stickyCurrentPanelIndexRef.current) {
        return;
      }

      if (stickyJumpingRef.current) {
        return;
      }

      transitionStickyPanel(panels, nextPanelIndex);
    };

    const ctx = gsap.context(() => {
      gsap.set(panels, { autoAlpha: 0 });
      gsap.set(panels[0], { autoAlpha: 1 });
      setActiveIndex(0);
      stickyCurrentPanelIndexRef.current = 0;

      ScrollTrigger.create({
        trigger: wrapperRef.current,
        start: "top top",
        end: "bottom bottom",
        pin: stickyRef.current,
        id: "section3-5-pin",
        // markers: {
        //   startColor: "#594DA0",
        //   endColor: "#594DA0",
        //   indent: 20,
        // },
        pinSpacing: false,
        anticipatePin: 1,
        invalidateOnRefresh: true,
        onLeaveBack: () => {
          if (stickyJumpingRef.current) return;
          showPanel(0);
        },
      });

      steps.forEach((step, index) => {
        ScrollTrigger.create({
          trigger: step,
          start: "top 25%",
          end: "top 25%",
          id: stickyContent[index].id,
          // markers: {
          //   startColor: index === 0 ? "#1d9bf0" : index === 1 ? "#22c55e" : "#f97316",
          //   endColor: index === 0 ? "#1d9bf0" : index === 1 ? "#22c55e" : "#f97316",
          //   indent: 60 + index * 28,
          // },
          onEnter: () => {
            showPanel(index);
          },
          onEnterBack: () => {
            showPanel(index);
          },
        });
      });
    }, wrapperRef);

    return () => {
      if (stickyJumpDelayTimerRef.current !== null) {
        window.clearTimeout(stickyJumpDelayTimerRef.current);
        stickyJumpDelayTimerRef.current = null;
      }
      stickyScrollTweenRef.current?.kill();
      stickyScrollTweenRef.current = null;
      ctx.revert();
    };
  }, [stickyContent.length]);

  useLayoutEffect(() => {
    if (
      !section6WrapperRef.current ||
      !section6StickyRef.current ||
      section6Content.length <= 1
    ) {
      return;
    }

    const panels = section6PanelsRef.current.filter(
      (panel): panel is HTMLDivElement => panel !== null
    );
    const steps = section6StepsRef.current.filter(
      (step): step is HTMLDivElement => step !== null
    );

    if (!panels.length || !steps.length) return;

    let currentPanelIndex = 0;

    const showPanel = (nextPanelIndex: number) => {
      if (nextPanelIndex === currentPanelIndex) return;

      gsap.to(panels[currentPanelIndex], {
        autoAlpha: 0,
        duration: 0.35,
        ease: "power2.out",
        overwrite: "auto",
      });

      gsap.fromTo(
        panels[nextPanelIndex],
        { autoAlpha: 0 },
        {
          autoAlpha: 1,
          duration: 0.45,
          ease: "power2.out",
          overwrite: "auto",
        }
      );

      currentPanelIndex = nextPanelIndex;
      setSection6ActiveIndex(nextPanelIndex);
    };

    const ctx = gsap.context(() => {
      gsap.set(panels, { autoAlpha: 0 });
      gsap.set(panels[0], { autoAlpha: 1 });
      setSection6ActiveIndex(0);

      ScrollTrigger.create({
        trigger: section6WrapperRef.current,
        start: "top top",
        end: "bottom bottom",
        pin: section6StickyRef.current,
        id: "section6-pin",
        // markers: {
        //   startColor: "#ffffff",
        //   endColor: "#ffffff",
        //   indent: 20,
        // },
        pinSpacing: false,
        anticipatePin: 1,
        invalidateOnRefresh: true,
        onLeaveBack: () => {
          showPanel(0);
        },
      });

      steps.forEach((step, index) => {
        ScrollTrigger.create({
          trigger: step,
          start: "top 25%",
          end: "top 25%",
          id: `section6-step-${index + 1}`,
          // markers: {
          //   startColor: index === 0 ? "#1d9bf0" : index === 1 ? "#22c55e" : "#f97316",
          //   endColor: index === 0 ? "#1d9bf0" : index === 1 ? "#22c55e" : "#f97316",
          //   indent: 60 + index * 28,
          // },
          onEnter: () => {
            showPanel(index);
          },
          onEnterBack: () => {
            showPanel(index);
          },
        });
      });
    }, section6WrapperRef);

    return () => {
      ctx.revert();
    };
  }, [section6Content.length]);


  return (
    <div id="IonAcqAppID">
      <div className={styles.gradientBkgd}></div>

      {/* ---------- Section 1 : The Command Center... ---------- */}
      <section className={styles.section1}>
        <div className={`${styles.maxWidthContainer} maxWidthContainer`}>
          <div className={styles.copyContainer}>
            <motion.span
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: "easeOut" }}
            >
              {parse(ionAcqAppSection1Data.overline)}
            </motion.span>
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2, ease: "easeOut" }}
            >
              {parse(ionAcqAppSection1Data.heading)}
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4, ease: "easeOut" }}
            >
              {parse(ionAcqAppSection1Data.body)}
            </motion.p>
          </div>
          <div className={styles.imgContainer}>
            <MotionImage
              src={ionAcqAppSection1Data.gallery[0].url}
              alt={ionAcqAppSection1Data.gallery[0].alt}
              width={500}
              height={500}
              priority
              fetchPriority="high"
              loading="eager"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6, ease: "easeOut" }}
            />
          </div>
        </div>
      </section>


      {/* ---------- Section 2 : Once spaces are mapped... ---------- */}
      <section ref={section2Ref} className={styles.section2}>
        <div className={`${styles.maxWidthContainer} maxWidthContainer`}>
          {/* <MotionImage
            className={styles.bkgdImg}
            src={ionAcqAppSection2Data.gallery[0].url}
            alt={ionAcqAppSection2Data.gallery[0].alt}
            width={690}
            height={559}
            priority
            style={{ y: section2BackgroundY }}
          /> */}

          <div className={styles.copyContainer}>
            <p className={styles.overline}>
              {parse(ionAcqAppSection2Data.overline)}
            </p>
            <h2>{parse(ionAcqAppSection2Data.heading)}</h2>
          </div>

          <div className={styles.svgArrowWrap}>
            <Image
              className={styles.svgArrowBase}
              src={ionAcqAppSection2Data.gallery[1].url}
              alt={ionAcqAppSection2Data.gallery[1].alt}
              width={10}
              height={100}
            />
            <MotionImage
              className={styles.svgArrowDraw}
              src={ionAcqAppSection2Data.gallery[1].url}
              alt={ionAcqAppSection2Data.gallery[1].alt}
              width={10}
              height={100}
              initial={{ opacity: 1, clipPath: "inset(0 0 100% 0)" }}
              animate={{
                opacity: 1,
                clipPath: [
                  "inset(0 0 100% 0)",
                  "inset(0 0 0% 0)",
                  "inset(100% 0 0% 0)",
                ],
              }}
              transition={{
                duration: 1.8,
                ease: "linear",
                repeat: Infinity,
                repeatDelay: 0.2,
                times: [0, 0.5, 1],
                repeatType: "loop",
              }}
            />
          </div>
        </div>
      </section>


      {/* ---------- Section 3 - 5 : Multi-Level Space Capture ---------- */}
      <section
        ref={wrapperRef}
        className={styles.stickyScrollWrapper}
        style={{ ["--sticky-panels" as string]: stickyContent.length }}
      >
        <div className={styles.stickyStepsTrack}>
          {stickyContent.map((item, index) => (
            <div
              key={`${item.id}-step`}
              ref={(element) => {
                stickyStepsRef.current[index] = element;
              }}
              className={styles.stickyStep}
              aria-hidden="true"
            />
          ))}
          <div className={styles.stickyStepTail} aria-hidden="true" />
        </div>

        <div ref={stickyRef} className={styles.stickyContent}>
          <div className={`${styles.maxWidthContainer} maxWidthContainer`}>
            <div className={styles.pagination}>
              {stickyContent.map((item, index) => (
                <button
                  key={item.id}
                  type="button"
                  className={`${styles.paginationItem} ${
                    index <= activeIndex
                      ? styles.pastPaginationItem
                      : styles.upcomingPaginationItem
                  }`}
                  onClick={() => scrollToStickyPanel(index)}
                  aria-label={`Scroll to panel ${index + 1}`}
                />
              ))}
            </div>

            <div className={styles.contentStage}>
              {stickyContent.map((item, index) => (
                <div
                  key={item.id}
                  ref={(element) => {
                    stickyPanelsRef.current[index] = element;
                  }}
                  className={`${styles.stickyPanel} ${
                    index === activeIndex ? styles.stickyPanelActive : ""
                  }`}
                  aria-hidden={index !== activeIndex}
                >
                  <div className={styles.copyContainer}>
                    <h3>{parse(item.data.heading)}</h3>
                    <p>{parse(item.data.body)}</p>
                  </div>
                  <div className={styles.imgContainer}>
                    <Image
                      src={item.data.gallery[0].url}
                      alt={item.data.gallery[0].alt}
                      fill
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ---------- Section 6 : How It Works ---------- */}
      <div
        ref={section6WrapperRef}
        className={styles.section6Wrapper}
        style={{ ["--section6-panels" as string]: section6Content.length }}
      >
        <div className={styles.section6StepsTrack}>
          {section6Content.map((item, index) => (
            <div
              key={`section6-step-${item.bulletNumber}-${index}`}
              ref={(element) => {
                section6StepsRef.current[index] = element;
              }}
              className={styles.section6Step}
              aria-hidden="true"
            />
          ))}
          <div className={styles.section6StepTail} aria-hidden="true" />
        </div>

        <section ref={section6StickyRef} className={styles.section6}>
          <div className={`${styles.maxWidthContainer} maxWidthContainer`}>
            {section6Heading && (
              <motion.h2
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.6, ease: "easeOut" }}
              >
                {parse(section6Heading.heading)}
              </motion.h2>
            )}

            <motion.div
              className={styles.bulletsContainer}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.6, delay: 0.2, ease: "easeOut" }}
            >
              <div className={styles.horiLine}></div>

              {section6Content.map((item, index) => {
                const isActive = index === section6ActiveIndex;

                return (
                  <motion.div
                    key={`${item.bulletNumber}-${index}`}
                    className={styles.bulletContainer}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-100px" }}
                    transition={{
                      duration: 0.45,
                      delay: 0.35 + index * 0.12,
                      ease: "easeOut",
                    }}
                  >
                    <div
                      className={`${styles.bulletNumber} ${
                        isActive ? styles.bulletNumberWhite : ""
                      }`}
                    >
                      {parse(item.bulletNumber)}
                    </div>
                    <p
                      className={`${styles.bulletText} ${
                        isActive ? styles.bulletTextWhite : ""
                      }`}
                    >
                      {parse(item.bulletText)}
                    </p>
                  </motion.div>
                );
              })}
            </motion.div>

            <div className={styles.section6ContentStage}>
              {section6Content.map((item, index) => (
                <div
                  key={`section6-content-${index}`}
                  ref={(element) => {
                    section6PanelsRef.current[index] = element;
                  }}
                  className={`${styles.section6Panel} ${
                    index === section6ActiveIndex ? styles.section6PanelActive : ""
                  }`}
                  aria-hidden={index !== section6ActiveIndex}
                >
                  <div className={styles.copyImgContainer}>
                    <div className={styles.copyContainer}>
                      <h3>{parse(item.heading)}</h3>
                      <p>{parse(item.body)}</p>
                    </div>

                    <div className={styles.imgContainer}>
                      <Image
                        src={item.gallery[0].url}
                        alt={item.gallery[0].alt}
                        width={450}
                        height={500}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>



      {/* ---------- Section 7 : Designed to Make Mapping Easier ---------- */}
      <section className={styles.section7}>
        <div className={`${styles.maxWidthContainer} maxWidthContainer`}>
          <h2>
            {parse(ionAcqAppSection7Data.heading)}
          </h2>

          <div className={styles.imgsContainer}>
            {ionAcqAppSection7Data.gallery.map((image, index) => (
              <motion.div
                className={styles.imgCopyContainer}
                key={`sdk-for-devs`+ index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{
                  duration: 0.6,
                  delay: index * 0.15,
                  ease: "easeOut",
                }}
              >
                {/* <MotionImage
                  key={image.url}
                  src={image.url}
                  alt={image.alt}
                  width={250}
                  height={250}
                  priority={index === 0}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{
                    duration: 0.6,
                    delay: 0.6 + (index * 0.1),
                    ease: "easeOut",
                  }}
                /> */}
                <div className={styles.imgContainer}>
                  <Image 
                    key={image.url}
                    src={image.url}
                    alt={image.alt}
                    fill
                  />
                </div>

                <p>{parse(image.title)}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>


      {/* ---------- Section 8 : Scale Your Mapping... ---------- */}
      <section ref={section8Ref} id="section8" className={`${styles.section8} section8`}>
        <div className={`${styles.maxWidthContainer} maxWidthContainer`}>
          <motion.div className={styles.bgImageContainer} style={{ y: section8BackgroundY }}>
            <Image
              className={styles.bgImage}
              src={ionAcqAppSection8Data.gallery[0].url}
              alt={ionAcqAppSection8Data.gallery[0].alt}
              fill
            />
          </motion.div>

          <div className={styles.copyBtnsContainer}>
            <div className={styles.copyContainer}>
              <h2>{parse(ionAcqAppSection8Data.heading)}</h2>
            </div>
            <div className={styles.btnsContainer}>
              <TransitionLink href="/contact-us">
                {parse(ionAcqAppSection8Data.button)}
              </TransitionLink>
              <TransitionLink href="/ecosystem/deployment-tools/cms">
                {parse(ionAcqAppSection8Data.button2)}
              </TransitionLink>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AcqAppView;
